import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import * as Sentry from "@sentry/react";
import { useTranslation } from '@/hooks/useTranslation';
import logoDark from "@/assets/logo-dark.png"

const AlbumResolution = () => {
  const { t } = useTranslation();
  const [albumSlug, setAlbumSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const navigate = useNavigate();

  const normalizeAlbumSlug = (value: string) => value.trim().toLowerCase();

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (showSearchForm && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearchForm]);

  const handleSearch = async () => {
    const normalizedSlug = normalizeAlbumSlug(albumSlug);

    if (!normalizedSlug) {
      toast({
        title: t('errorTitle'),
        description: t('enterAlbumName'),
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('albums')
        .select('id, slug')
        .eq('slug', normalizedSlug)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        Sentry.captureMessage(
          `Error fetching album: ${error instanceof Error ? error.message : String(error)}`,
          "error"
        );
        console.error('Error fetching album:', error);
        toast({
          title: t('errorTitle'),
          description: t('searchAlbumError'),
          variant: "destructive"
        });
        return;
      }

      if (!data) {
        toast({
          title: t('errorTitle'),
          description: t('albumNotFound'),
          variant: "destructive"
        });
        return;
      }

      // Redirect to album inicio page
      navigate(`/${data.slug}/inicio`);
    } catch (error) {
      Sentry.captureMessage(
        error instanceof Error ? error.message : String(error),
        "error"
      );
      console.error('Error:', error);
      toast({
        title: t('errorTitle'),
        description: t('unexpectedSearchError'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div
      className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-8 relative"
      style={{
        backgroundImage: 'url(https://bloomkeep.b-cdn.net/assets/default-cover.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-background/60"></div>

      {/* Content container */}
      <div className="relative z-10 w-full max-w-md flex flex-col items-center">
        {/* Logo */}
        <div className="animate-fade-in mb-8" style={{ animationDelay: '300ms', animationDuration: '500ms', animationFillMode: 'both' }}>
          <img
            src={logoDark}
            alt="Logo"
            className="h-32 md:h-40 w-auto select-none pointer-events-none"
          />
        </div>

        {/* Initial button or search form */}
        <div className={`${showSearchForm ? "bg-background/50" : ""} backdrop-blur-sm rounded-2xl p-8 text-center w-full animate-fade-in`} style={{ animationDelay: '500ms', animationDuration: '500ms', animationFillMode: 'both' }}>
          {!showSearchForm ? (
            <Button
              onClick={() => setShowSearchForm(true)}
              size="lg"
              className="h-12 text-lg font-medium bg-purple-300 hover:bg-purple-500 hover:text-white text-primary-foreground rounded-xl transition-colors px-8"
            >
              Acceder a un álbum
            </Button>
          ) : (
            <div className="space-y-6">

              <div className="space-y-4">
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder={t('enterAlbumNamePlaceholder')}
                  value={albumSlug}
                  onChange={(e) => setAlbumSlug(normalizeAlbumSlug(e.target.value))}
                  onKeyPress={handleKeyPress}
                  className="text-center text-lg h-12 border border-purple-300"
                  disabled={loading}
                />

                <Button
                  onClick={handleSearch}
                  disabled={loading}
                  size="lg"
                  className="w-full h-12 text-lg font-medium bg-purple-300 hover:bg-purple-500 hover:text-white text-primary-foreground rounded-xl transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      {t('searching')}
                    </>
                  ) : (
                    t('search')
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Copyright */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-10">
        <p className="text-sm text-foreground/70 text-center">
          © 2025 BloomKeep. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default AlbumResolution;